import React, { useCallback, useRef, useState } from 'react'

import cx from 'classnames'
import { CallHistoryMethodAction } from 'connected-react-router'
import { ConversationsSectionsLengthListMenu } from 'conversations/components/NewChatSidebar/ConversationsSectionsLengthListMenu'
import useBadgeCounts, { BadgeCounts } from 'conversations/hooks/useBadgeCounts'
import useOnClickOutside from 'conversations/hooks/useOnClickOutside'
import { useCurrentUserId } from 'conversations/hooks/useOrgUser'
import useSectionNotifications, {
  SectionNotification,
} from 'conversations/hooks/useSectionNotifications'
import { getInboxes } from 'conversations/modules/org/selectors'
import {
  getCurrentInboxId,
  getMarkAllAsReadPreference,
  updatedUserNotificationsBySubscription,
} from 'conversations/modules/user/selectors'
import { usePopper } from 'react-popper'
import { useDispatch, useSelector } from 'react-redux'
import { Dispatch } from 'redux'
import { receiveBadgeCounts } from 'shared/store/actions/conversation'
import { track } from 'shared/store/actions/tracking'
import {
  closeAllMyConversationsAsync,
  patchNotificationSettingsBySubscription,
  setPreference,
} from 'shared/store/actions/user'
import {
  toggleCreateConvoSectionModal,
  toggleEditConvoSectionModal,
  toggleViewConvoSectionModal,
} from 'shared/store/actions/user/sections'
import { Inbox } from 'shared/typings/bootstrap'
import {
  ConversationSidebarSectionConfig,
  DEFAULT_FILTER_TYPES,
} from 'shared/typings/conversationsSidebar/Filters'

import { Portal } from '@driftt/conversation-components'
import { ReactComponent as Arrow } from '@driftt/conversation-components/icons/arrow.svg'
import { Icon } from '@driftt/tide-icons'
import { access, receiveConversationBadgeCounts } from '@driftt/traffic-control'

import { RootAction } from 'typesafe-actions'

import { BadgeCountsSelector } from '../ConversationListHeader'

import styles from './styles.module.css'
import utilityStyles from 'conversations/css/utility.module.css'

interface Props {
  children: JSX.Element
  sectionConfig?: ConversationSidebarSectionConfig
  active: boolean
  setActive: (active: boolean) => void
  deleteSection: () => void
  onSectionLengthChange: (length: number) => void
}
interface MenuContentProps {
  sectionConfig?: ConversationSidebarSectionConfig
  onClick: (e: React.MouseEvent) => void
  deleteSection: () => void
  onSectionLengthChange: (length: number) => void
}
const ConversationsListMenuContent: React.FC<MenuContentProps> = ({
  sectionConfig,
  onClick,
  deleteSection,
  onSectionLengthChange,
}): JSX.Element | null => {
  const sectionNotification = useSectionNotifications(sectionConfig?.subscriptionId)
  const notificationsBySubscription = useSelector(updatedUserNotificationsBySubscription)
  const dispatch = useDispatch<Dispatch<RootAction | CallHistoryMethodAction>>()
  const [menuActive, setMenuActive] = useState(false)
  const currentUserId = useCurrentUserId()
  const currentInboxId = useSelector(getCurrentInboxId)
  const inboxes = useSelector(getInboxes)
  const markAllAsReadPrefs = useSelector(getMarkAllAsReadPreference)
  const badgeCounts = useBadgeCounts()
  const isMyConversations = sectionConfig?.type === 'my-convos'
  const isDefaultFilter =
    DEFAULT_FILTER_TYPES.includes(sectionConfig?.type || '') || isMyConversations

  const toggleNotifications = useCallback(() => {
    if (!sectionConfig?.subscriptionId) {
      return
    }
    dispatch(
      patchNotificationSettingsBySubscription({
        ...notificationsBySubscription,
        [sectionConfig.subscriptionId]: {
          allNotificationsForSubscriptionEnabled: sectionNotification !== SectionNotification.ON,
          subscriptionId: sectionConfig.subscriptionId,
        },
      })
    )
  }, [dispatch, notificationsBySubscription, sectionNotification, sectionConfig])

  const closeAllMyConversations = useCallback(() => {
    currentUserId && dispatch(closeAllMyConversationsAsync.request({ id: currentUserId }))
  }, [currentUserId, dispatch])

  const setModalOpen = useCallback(() => dispatch(toggleCreateConvoSectionModal()), [dispatch])

  const updateBadgingPreferences = () => {
    const now = Date.now()
    const updateAllInboxes = (): Record<number, number> =>
      inboxes.reduce((acc: Record<number, number>, inbox: Inbox) => {
        acc[inbox.id] = now
        return acc
      }, {})

    // if inbox id is empty, then 'all inboxes' is selected
    const prefValue = currentInboxId
      ? { ...markAllAsReadPrefs, [currentInboxId]: now }
      : updateAllInboxes()

    const formattedPrefs = Object.entries(prefValue).map(([inboxId, timestamp]) => ({
      inboxId: parseInt(inboxId),
      timestamp,
    }))

    dispatch(
      setPreference({
        preference: 'lastMarkAllAsRead',
        value: formattedPrefs,
      })
    )
  }

  const updateBadgingData = () => {
    const clearSingleInboxBadgeCounts: Partial<BadgeCountsSelector> = { ...badgeCounts }

    delete clearSingleInboxBadgeCounts['total']

    const singleInboxBadgeCounts = clearSingleInboxBadgeCounts as BadgeCounts
    if (currentInboxId) singleInboxBadgeCounts[currentInboxId] = {}

    const updatedBadgingData: BadgeCounts = currentInboxId
      ? singleInboxBadgeCounts
      : ({} as BadgeCounts)

    const updatedConvoBadgeCount = Object.values(updatedBadgingData).reduce(
      (acc, conversations) => acc + Object.keys(conversations).length,
      0
    )

    dispatch(receiveBadgeCounts(updatedBadgingData))
    dispatch(receiveConversationBadgeCounts(updatedConvoBadgeCount))
  }

  const markAllMyConvosAsRead = () => {
    dispatch(
      track({
        name: 'Badging - Mark all as read',
        properties: {
          inbox: currentInboxId || 'all',
        },
      })
    )
    updateBadgingPreferences()
    updateBadgingData()
  }

  const sharedSection = sectionConfig?.shared ?? false
  const canEditShared = access.hasFeatures([
    { featureName: 'conversations_shared_sections', accessLevel: 'WRITE' },
  ])

  const viewOrEditSection = (section: ConversationSidebarSectionConfig) => {
    const view = (
      <div
        className={cx(styles.edit, styles.item)}
        onClick={() => dispatch(toggleViewConvoSectionModal(section.id))}
        data-ocweb-id="ViewConversationSectionMenuButton">
        View {section.title}
      </div>
    )
    const edit = (
      <div
        className={cx(styles.edit, styles.item)}
        onClick={() => dispatch(toggleEditConvoSectionModal(section.id))}
        data-ocweb-id="EditConversationSectionMenuButton">
        Edit {section.title}
      </div>
    )
    switch (true) {
      case !isDefaultFilter && !sharedSection:
      case !isDefaultFilter && sharedSection && canEditShared:
        return edit
      case !isDefaultFilter && sharedSection:
        return view
      default:
        return null
    }
  }

  const canDelete =
    (!isDefaultFilter && sharedSection && canEditShared) || (!isDefaultFilter && !sharedSection)

  return (
    <div onClick={onClick} className={styles.menu}>
      {sectionConfig && (
        <>
          {isMyConversations && (
            <div
              className={cx(styles.rename, styles.item)}
              onClick={markAllMyConvosAsRead}
              data-ocweb-id="MarkAllMyConvosAsRead">
              Mark all as read
            </div>
          )}
          {sectionNotification !== SectionNotification.NONE && (
            <div
              className={cx(styles.rename, styles.item)}
              onClick={toggleNotifications}
              data-ocweb-id="ToggleSectionNotificationsButton">
              {sectionNotification === SectionNotification.ON
                ? 'Turn off notifications'
                : 'Turn on notifications'}
            </div>
          )}
          {isMyConversations && (
            <div
              className={cx(styles.rename, styles.item)}
              onClick={closeAllMyConversations}
              data-ocweb-id="CloseAllConversationsMenuButton">
              Close all my conversations
            </div>
          )}
          {!sharedSection && (
            <div
              className={cx(styles.sectionLength, styles.item, styles.topBorder, {
                [styles.bottomBorder]: !isDefaultFilter,
              })}
              onMouseEnter={() => setMenuActive(true)}
              onMouseLeave={() => setMenuActive(false)}>
              <ConversationsSectionsLengthListMenu
                active={menuActive}
                setActive={setMenuActive}
                sectionConfig={sectionConfig}
                onSectionLengthChange={onSectionLengthChange}>
                <div className={styles.sectionLengthRow}>
                  <div>Update section length</div>
                  <Arrow
                    width="8"
                    height="8"
                    className={cx(utilityStyles.rotate270, styles.arrow)}
                  />
                </div>
              </ConversationsSectionsLengthListMenu>
            </div>
          )}
          {viewOrEditSection(sectionConfig)}
          {canDelete && (
            <div
              className={cx(styles.delete, styles.item)}
              onClick={deleteSection}
              data-ocweb-id="DeleteConversationSectionMenuButton">
              Delete {sectionConfig.title}
            </div>
          )}
        </>
      )}

      <div
        className={cx(styles.newSection, styles.item, styles.topBorder)}
        onClick={setModalOpen}
        data-ocweb-id="NewConversationSectionMenuButton">
        <div className={cx(styles.newSectionContent)}>
          <Icon name="plus" width={8} height={8} className={styles.newSectionIcon} />
          Create new section
        </div>
      </div>
    </div>
  )
}
export const ConversationsListMenu: React.FC<Props> = ({
  children,
  sectionConfig,
  active,
  setActive,
  deleteSection,
  onSectionLengthChange,
}): JSX.Element | null => {
  const popperElem = useRef<HTMLDivElement | null>(null)
  const referenceElem = useRef<HTMLDivElement | null>(null)

  const {
    styles: popperStyles,
    attributes,
    forceUpdate,
  } = usePopper(referenceElem.current, popperElem.current, {
    placement: 'bottom-start',
    modifiers: [
      {
        name: 'offset',
        options: {
          offset: [0, 8],
        },
      },
    ],
  })
  useOnClickOutside(popperElem, (e: MouseEvent) => {
    e.stopPropagation()
    setActive(false)
  })

  return (
    <>
      <div
        ref={referenceElem}
        onClick={(e) => {
          forceUpdate && forceUpdate()
          setActive(true)
          e.stopPropagation()
        }}>
        {children}
      </div>
      <Portal>
        <div
          ref={popperElem}
          className={cx(styles.menuContainer, { [styles.hiddenStyle]: !active })}
          style={popperStyles.popper}
          {...attributes.popper}>
          {active && (
            <ConversationsListMenuContent
              sectionConfig={sectionConfig}
              deleteSection={deleteSection}
              onClick={(e) => {
                setActive(false)
                e.stopPropagation()
              }}
              onSectionLengthChange={onSectionLengthChange}
            />
          )}
        </div>
      </Portal>
    </>
  )
}
